import { Camera } from '@phosphor-icons/react';
import { Button, Loader, VText } from '@vake/ui-kit';

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    useGetInsights,
    useGetNewestInsight,
    useGetSanctionedVesselPathfinderQuery,
    useGetSanctionedVesselQuery,
    useGetSanctionedVesselTagsQuery,
} from '../requests/actor-mode';
import { Flag } from '../shared/components/flag';
import { formatRelativeTime } from '../shared/components/time';
import { ErrorHandler } from '../utils/errorHandling';
import { externalMarineTrafficByIMO } from '../utils/mmsiExternalCall';
import { InsightHolders } from './RecentInsightDashboard';
import { TagView } from './Report/components/OverwatchRight';
import { SendToMattermost } from './Report/components/send-to-mattermost';

const ActorModeVessel = () => {
    const location = useLocation();
    const vesselId = location.pathname.split('/').pop() || '';
    const { data: sanctionedVessel, isError, isLoading } = useGetSanctionedVesselQuery(vesselId);
    const {
        data: sanctionedVesselTags,
        isLoading: loadingTags,
        isError: errorTags,
        isSuccess: tagsSuccess,
    } = useGetSanctionedVesselTagsQuery(vesselId);
    const {
        data: sanctionedVesselPathfinders,
        isLoading: loadingPathfinders,
        isError: errorPathfinders,
        isSuccess: pathfindersSuccess,
    } = useGetSanctionedVesselPathfinderQuery(vesselId);
    const {
        data: sanctionedVesselInsightsInNorway,
        isLoading: loadingInsightsInNorway,
        isError: errorInsightsInNorway,
        isSuccess: insightsInNorwaySuccess,
    } = useGetInsights(vesselId, true, true);
    const [loadInsightsOutsideNorway, setLoadInsightsOutsideNorway] = useState<boolean>(false);
    const {
        data: sanctionedVesselInsightsOutsideNorway,
        isLoading: loadingInsightsOutsideNorway,
        isError: errorInsightsOutsideNorway,
        isSuccess: insightsOutsideNorwaySuccess,
    } = useGetInsights(vesselId, loadInsightsOutsideNorway, false);
    const {
        data: newestInsight,
        isLoading: newestInsightLoading,
        isError: errorNewestInsight,
        isSuccess: insightsNewestSuccess,
    } = useGetNewestInsight(vesselId);
    const overwatch_norway_2 = '/overwatch/b1b33661-cb7a-43ff-add0-13fe0fd5b1b5';
    const flag = isLoading || !sanctionedVessel ? '' : sanctionedVessel.flag;
    const name = isLoading || !sanctionedVessel ? '' : sanctionedVessel.name;
    const imo = isLoading || !sanctionedVessel ? 0 : sanctionedVessel.imo;
    const mmsi = isLoading || !sanctionedVessel ? '' : sanctionedVessel.mmsi;
    const last_updated_ais =
        isLoading || !sanctionedVessel ? '' : formatRelativeTime(new Date(sanctionedVessel.last_updated_ais));
    const last_position = isLoading || !sanctionedVessel ? '' : sanctionedVessel.last_position;
    const newestOverwatchLink =
        newestInsightLoading || !newestInsight || isLoading || !sanctionedVessel
            ? overwatch_norway_2
            : `${overwatch_norway_2}?date=${new Date(sanctionedVessel.last_updated_ais)}&confidence=92&uuid=${
                  newestInsight.insight_uuid
              }`;
    return (
        <div className="flex flex-col p-16 gap-16 max-w-[1536px] mx-auto">
            <ErrorHandler isError={isError} errorMessage={'Loading vessel data failed'}>
                <VText>Error loading vessel data.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorTags} errorMessage={'Loading tags failed'}>
                <VText>Error loading tags.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorPathfinders} errorMessage={'Loading pathfinders failed'}>
                <VText>Error loading pathfinders.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorInsightsInNorway} errorMessage={'Loading insights in Norway failed'}>
                <VText>Error loading insights in Norway.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorInsightsOutsideNorway} errorMessage={'Loading insights outside Norway failed'}>
                <VText>Error loading insights outside Norway.</VText>
            </ErrorHandler>
            <ErrorHandler isError={errorNewestInsight} errorMessage={'Loading newest insight failed'}>
                <VText>Error loading newest insight.</VText>
            </ErrorHandler>
            <div className="flex items-center gap-4">
                <Flag countryCode={flag} width={26} height={20} />
                <VText variant="H1">{name}</VText>
                <VText variant="H1">IMO: {imo}</VText>
                <VText variant="H1">MMSI: {mmsi}</VText>
            </div>
            <div className="flex flex-col gap-2 max-w-80">
                <VText>Last Updated: {last_updated_ais}</VText>
                <VText>Last Position (lat, lon): {last_position}</VText>
                {newestInsightLoading && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {insightsNewestSuccess && (
                    <Link to={newestOverwatchLink}>
                        <Button text="Open in Overwatch" />
                    </Link>
                )}
                <SendToMattermost
                    textOnButton="Order Satellite Image"
                    iconOnButton={<Camera size={16} />}
                    textToSend={`asd`}
                    successMessage={`Thank you, we've received your order. We'll send you the image soon.`}
                />
                <Button
                    text="Open MarineTraffic"
                    variant="neutral"
                    onClick={() => window.open(externalMarineTrafficByIMO(imo), '_blank')}
                />
            </div>
            <div className="flex flex-col gap-2 max-w-60">
                <VText>Actions last 6 months</VText>
                {loadingTags && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {tagsSuccess && (
                    <TagView
                        tags={
                            sanctionedVesselTags && sanctionedVesselTags.length > 0
                                ? sanctionedVesselTags
                                : ['Nothing Suspicious']
                        }
                    />
                )}
            </div>
            <div className="flex flex-col gap-2 max-w-60">
                <VText>Pathfinder reports</VText>
                {loadingPathfinders && (
                    <div className="flex justify-center">
                        <Loader size={16} />
                    </div>
                )}
                {pathfindersSuccess && (
                    <>
                        {sanctionedVesselPathfinders && sanctionedVesselPathfinders.length > 0 ? (
                            sanctionedVesselPathfinders.map((pathfinder, index) => (
                                <Link to={`/pathfinder/${pathfinder.url}`} key={index}>
                                    <Button text={pathfinder.name} variant="neutral" />
                                </Link>
                            ))
                        ) : (
                            <VText significance="secondary">No pathfinder reports available</VText>
                        )}
                    </>
                )}
            </div>
            <div>
                <VText>Images of vessel in Norway last 6 months</VText>
                {loadingInsightsInNorway && (
                    <div className="flex">
                        <Loader size={16} />
                    </div>
                )}
                {insightsInNorwaySuccess && sanctionedVesselInsightsInNorway && (
                    <InsightHolders insights={sanctionedVesselInsightsInNorway} />
                )}
            </div>
            <div>
                <VText>Images of vessel outside Norway last 6 months</VText>
                {loadingInsightsOutsideNorway && (
                    <div className="flex">
                        <Loader size={16} />
                    </div>
                )}
                {loadInsightsOutsideNorway ? (
                    <>
                        {insightsOutsideNorwaySuccess && sanctionedVesselInsightsOutsideNorway && (
                            <InsightHolders insights={sanctionedVesselInsightsOutsideNorway} />
                        )}
                    </>
                ) : (
                    <Button text="Load" variant="neutral" onClick={() => setLoadInsightsOutsideNorway(true)} />
                )}
            </div>
            {/* {newestInsight && <ClearviewView uuid={newestInsight.insight_uuid} />} */}
            {/* <VText>AIS gaps</VText> */}
            {/* <VText>Flag history</VText> */}
        </div>
    );
};
export default ActorModeVessel;
